import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronLeft, BookOpen, Loader2 } from 'lucide-react';
import Navigation from './Navigation';
import CopyButton from './CopyButton';

/**
 * GuideView Component
 * 
 * Full-page view for displaying guide content with VitePress-style layout.
 * Features:
 * - Left sidebar with table of contents extracted from markdown headings
 * - Main content area with markdown rendering
 * - Breadcrumb navigation
 * - Mobile responsive design
 * - Error handling for missing guides
 * - Copy buttons for code blocks
 * - Smooth scrolling to headings via table of contents
 */
const GuideView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [tableOfContents, setTableOfContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchGuide();
  }, [slug]);

  // Extract headings from markdown content to create table of contents
  const extractHeadings = (markdownContent) => {
    if (!markdownContent) return [];
    
    const headingRegex = /^(#{1,6})\s+(.+)$/gm;
    const headings = [];
    let match;
    
    while ((match = headingRegex.exec(markdownContent)) !== null) {
      const level = match[1].length; // Number of # characters
      const text = match[2].trim();
      const id = text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
      
      headings.push({
        level,
        text,
        id
      });
    }
    
    return headings;
  };

  const fetchGuide = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/resources/guide/${slug}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setGuide(data);
        // Extract headings for table of contents
        const headings = extractHeadings(data.markdown_content);
        setTableOfContents(headings);
      } else if (response.status === 404) {
        setError('Guide not found');
        // Redirect to resources after a short delay
        setTimeout(() => navigate('/resources'), 2000);
      } else {
        setError('Failed to load guide');
      }
    } catch (error) {
      console.error('Error fetching guide:', error);
      setError('Failed to load guide');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
            <p className="text-gray-600 font-medium">Loading guide...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <BookOpen className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Guide Not Found</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/resources')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Back to Resources
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!guide) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 overflow-x-hidden">
      <Navigation />
      
      <div className="flex h-screen pt-16">
        {/* Left Sidebar - Hidden on mobile */}
        <aside className="hidden lg:block w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <button
              onClick={() => navigate('/resources')}
              className="w-full flex items-center justify-center bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 mb-6 transition-colors font-medium"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Resources
            </button>
            
            <h2 className="font-bold text-lg mb-4 flex items-center">
              <BookOpen className="w-5 h-5 mr-2" />
              Table of Contents
            </h2>
            
            {/* Table of Contents */}
            {tableOfContents.length > 0 ? (
              <nav className="space-y-1">
                {tableOfContents.map((heading, index) => (
                  <a
                    key={index}
                    href={`#${heading.id}`}
                    className={`block py-2 px-3 rounded-md text-sm transition-colors hover:bg-gray-100 ${
                      heading.level === 1 ? 'font-semibold text-gray-900' :
                      heading.level === 2 ? 'font-medium text-gray-800 ml-2' :
                      'text-gray-700 ml-4'
                    }`}
                  >
                    {heading.text}
                  </a>
                ))}
              </nav>
            ) : (
              <p className="text-gray-500 text-sm">No headings found</p>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="max-w-4xl mx-auto px-8 py-12">
            {/* Mobile Back Button */}
            <div className="lg:hidden mb-6">
              <button
                onClick={() => navigate('/resources')}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Resources
              </button>
            </div>
            
            {/* Breadcrumb */}
            <div className="text-sm text-gray-500 mb-4">
              Resources {guide.category_names && guide.category_names.length > 0 && 
                <> / {guide.category_names[0]}</>
              }
            </div>
            
            {/* Guide Title */}
            <h1 className="text-4xl font-bold mb-4 text-gray-900">{guide.title}</h1>
            
            {/* Guide Description */}
            {guide.description && (
              <p className="text-lg text-gray-600 mb-8">{guide.description}</p>
            )}
            
            {/* Markdown Content */}
            <div className="prose prose-lg max-w-none prose-headings:font-bold prose-h1:text-4xl prose-h1:mb-4 prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-p:mb-4 prose-li:mb-2 prose-table:border-collapse prose-th:border prose-th:border-gray-300 prose-th:bg-gray-100 prose-th:p-3 prose-td:border prose-td:border-gray-300 prose-td:p-3 prose-strong:font-semibold prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:pl-4 prose-blockquote:italic">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  // Custom heading renderer to add IDs for table of contents
                  h1({ children, ...props }) {
                    const id = String(children).toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
                    return <h1 id={id} {...props}>{children}</h1>;
                  },
                  h2({ children, ...props }) {
                    const id = String(children).toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
                    return <h2 id={id} {...props}>{children}</h2>;
                  },
                  h3({ children, ...props }) {
                    const id = String(children).toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
                    return <h3 id={id} {...props}>{children}</h3>;
                  },
                  h4({ children, ...props }) {
                    const id = String(children).toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
                    return <h4 id={id} {...props}>{children}</h4>;
                  },
                  h5({ children, ...props }) {
                    const id = String(children).toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
                    return <h5 id={id} {...props}>{children}</h5>;
                  },
                  h6({ children, ...props }) {
                    const id = String(children).toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
                    return <h6 id={id} {...props}>{children}</h6>;
                  },
                  table({ children, ...props }) {
                    return (
                      <div className="w-screen max-w-[100vw] -mx-8 sm:mx-0 overflow-x-auto">
                        <table className="min-w-full table-auto" {...props}>
                          {children}
                        </table>
                      </div>
                    );
                  },
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || '');
                    const language = match ? match[1] : '';
                    
                    if (!inline) {
                      // This is a code block (```)
                      return (
                        <div className="relative">
                          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                            <code className={className} {...props}>
                              {children}
                            </code>
                          </pre>
                          <CopyButton textToCopy={String(children).replace(/\n$/, '')} />
                        </div>
                      );
                    }
                    
                    // This is inline code (`)
                    return (
                      <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm" {...props}>
                        {children}
                      </code>
                    );
                  }
                }}
              >
                {guide.markdown_content}
              </ReactMarkdown>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default GuideView;
